import { z } from 'zod';

/**
 * Read-only view of the deployment's transactional email configuration,
 * surfaced on the platform-admin "Email" settings page.
 *
 * Everything here is non-secret: it reports whether email is wired and how,
 * never the Customer.io API key itself. Email is configured through
 * environment variables (see `EmailService` / `createEmailService`), so this
 * is a status view, not an editable form.
 */
export const EmailSettingsSchema = z
  .object({
    /** True when a transport is wired and email will actually be delivered. */
    enabled: z.boolean(),
    /** Delivery transport name (e.g. `customer.io`), or `null` when disabled. */
    transport: z.string().nullable(),
    /** Configured sender address (`EMAIL_FROM`), or `null` when unset. */
    fromAddress: z.string().nullable(),
    /** Customer.io data-residency region (`us` / `eu`). */
    region: z.string(),
    /** Whether a Customer.io API key is configured. Never the key itself. */
    apiKeyConfigured: z.boolean(),
    /** Whether the `EMAIL_SENDING_DISABLED` kill switch is on. */
    sendingDisabled: z.boolean(),
    /** Registered template keys the deployment can send (e.g. `org-invite`). */
    templates: z.array(z.string()),
  })
  .strict()
  .meta({ id: 'EmailSettings' });
export type EmailSettings = z.infer<typeof EmailSettingsSchema>;
